import {ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AiChatFacade} from '../../ai-chat.facade';
import {AssetComponent} from "@components/mainmenu/reports-menu/ai-reports/ai-reports-sub-menu/asset/asset.component";
import {AssetListComponent} from "@components/mainmenu/reports-menu/ai-reports/ai-reports-sub-menu/asset/asset-list/asset-list.component";
import {CommonComponentModule} from "@components/common/common.module";
import {AssetMetaPipe} from 'src/app/pipes/AIReports/asset-meta.pipe';
import {takeUntilDestroyed, toObservable, toSignal} from '@angular/core/rxjs-interop';
import {catchError, combineLatest, concat, finalize, forkJoin, map, of, switchMap} from 'rxjs';
import {ClientAssetService} from 'src/app/services/AIReports/client-asset-service';
import {ReportsMenuNavigationService} from 'src/app/services/UI/reports-menu-navigation.service';
import {ViewReportMode} from 'src/app/common/enums/enums';
import {ToastComponent} from '@components/common/toast/toast.component';
import {Asset} from 'src/app/common/models/ai-reports/shared/assets';

/** Main-asset types the publish pipeline has no HTML-fragment normalization for yet. */
const UNSUPPORTED_PUBLISH_TYPES: Asset['type'][] = ['video'];

/** Backend column limit for a published report's name — clamp the AI-generated chat title before sending. */
const REPORT_NAME_MAX_LENGTH = 255;

/** C0 control bytes and Unicode bidi override characters — stripped from the AI-generated report name before publish. */
const UNSAFE_REPORT_NAME_CHARS = /[\u0000-\u001F\u202A-\u202E\u2066-\u2069]/g;

/**
 * Renders the AI-generated report assets (images, graphs, etc.) on the left side.
 *
 * Shows assets from the latest AI result, or all assets if no result yet.
 * Displays a loading spinner while a response streams.
 * Resolves asset URLs supporting multiple backends (base64, storage paths, URLs).
 */
@Component({
  selector: 'app-chat-report-display',
  standalone: true,
  imports: [CommonModule, TranslateModule, AssetComponent, AssetListComponent, CommonComponentModule, AssetMetaPipe, ToastComponent],
  templateUrl: './chat-report-display.component.html',
  styleUrl: './chat-report-display.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatReportDisplayComponent {
  /**
   * Facade providing assets and loading state.
   *
   * Template reads:
   * - `facade.displayAssets()` — rendered assets
   * - `facade.isAssetsLoading()` — loading spinner
   * - `facade.assets()` — for resolveAssetUrl validation
   */
  protected readonly facade = inject(AiChatFacade);
  protected readonly showAllAssets = signal(false);

  private readonly clientAssetService = inject(ClientAssetService);
  private readonly reportsMenuNavigation = inject(ReportsMenuNavigationService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  // The ID of the url/base64 background asset that a visjs in the result set renders
  // via CSS — suppressed from standalone rendering to avoid duplication.
  // Null when no visjs is present or when no url/base64 sibling exists.
  protected readonly visjsBackgroundId = toSignal(
    combineLatest([
      toObservable(this.facade.resultAssets),
      toObservable(this.facade.activeThreadId)
    ]).pipe(
      switchMap(([ids, threadId]) => {
        if (!ids.length || !threadId) return of(null);
        return forkJoin(
          ids.map(id =>
            this.clientAssetService.getMeta(threadId, id).pipe(
              map(a => ({ id, type: a?.type ?? null })),
              catchError(() => of({ id, type: null }))
            )
          )
        ).pipe(
          map(metas => {
            if (!metas.some(m => m.type === 'visjs')) return null;
            return metas.find(m => m.type === 'url' || m.type === 'base64')?.id ?? null;
          })
        );
      })
    ),
    { initialValue: null }
  );

  toggleAllAssets(): void {
    this.showAllAssets.update(v => !v);
  }

  // ── Export FAB ──────────────────────────────────────────────────────

  /** Type of the current main asset, resolved for the publish-support check below. */
  private readonly mainAssetType = toSignal(
    combineLatest([
      toObservable(this.facade.mainAssetId),
      toObservable(this.facade.activeThreadId)
    ]).pipe(
      switchMap(([mainAssetId, threadId]) => {
        if (!mainAssetId || !threadId) return of(null);
        return concat(
          of(null),
          this.clientAssetService.getMeta(threadId, mainAssetId).pipe(
            map(asset => asset?.type ?? null),
            catchError(() => of(null))
          )
        );
      })
    ),
    { initialValue: null }
  );

  protected readonly isPublishDisabled = computed(() => {
    const type = this.mainAssetType();
    return !this.facade.mainAssetId() || !type || UNSUPPORTED_PUBLISH_TYPES.includes(type);
  });

  /** Backed by reportsMenuNavigation rather than component state, since this component is destroyed on every tab switch. */
  protected readonly isPublishing = computed(() => {
    const threadId = this.facade.activeThreadId();
    const assetId = this.facade.mainAssetId();
    return !!threadId && !!assetId && this.reportsMenuNavigation.isInFlight(threadId, assetId);
  });

  protected readonly publishOutcome = signal<'success' | 'error' | null>(null);

  protected readonly fabTitle = computed(() => {
    this._langChange(); // re-translate on language change
    if (this.isPublishing()) return this.translate.instant('aiReport.lbl_export_in_progress');
    if (this.isPublishDisabled()) return this.translate.instant('aiReport.lbl_export_not_supported');
    return this.translate.instant('lbl_publish');
  });

  protected readonly toastMessage = computed(() => {
    this._langChange(); // re-translate on language change
    return this.translate.instant(this.publishOutcome() === 'success' ? 'aiReport.lbl_export_submitted' : 'aiReport.lbl_export_failed');
  });

  protected readonly toastActionLabel = computed(() => {
    this._langChange(); // re-translate on language change
    return this.publishOutcome() === 'success' ? this.translate.instant('lbl_published_reports') : null;
  });

  protected publishReport(): void {
    const threadId = this.facade.activeThreadId();
    const assetId = this.facade.mainAssetId();
    if (this.isPublishDisabled() || this.isPublishing() || !threadId || !assetId) return;

    this.reportsMenuNavigation.markInFlight(threadId, assetId);
    this.publishOutcome.set(null);
    const reportName = (this.facade.mainAssetName() ?? this.translate.instant('aiReport.lbl_default_report_name'))
      .replace(UNSAFE_REPORT_NAME_CHARS, '')
      .slice(0, REPORT_NAME_MAX_LENGTH)
      .trim();

    this.clientAssetService.publish(threadId, assetId, { template: 1, reportName }).pipe(
      finalize(() => this.reportsMenuNavigation.clearInFlight(threadId, assetId)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => this.publishOutcome.set('success'),
      error: () => this.publishOutcome.set('error'),
    });
  }

  protected openReportManager(): void {
    this.reportsMenuNavigation.requestViewMode(ViewReportMode.Published);
    this.publishOutcome.set(null);
  }

  protected dismissToast(): void {
    this.publishOutcome.set(null);
  }
}
